import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useToast } from "@/hooks/useToast";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9kd4prh5tg6sock720";

const CONTACT_CARDS = [
  {
    icon: "ri-mail-line",
    title: "内容与投稿",
    value: "hello@hualidewaichu.com",
    desc: "剧情补充、演员资料修正与投稿，我们会在一到两个工作日内回复。",
  },
  {
    icon: "ri-wechat-line",
    title: "社群与日常",
    value: "微信：huali_film",
    desc: "加入观影讨论群，第一时间获取剧集更新时间与站内更新提醒。",
  },
  {
    icon: "ri-map-pin-line",
    title: "编辑部地址",
    value: "上海市静安区影像街 27 号 3F",
    desc: "仅接受预约到访，来访前请先通过邮件确认时间。",
  },
];

const FAQS = [
  {
    q: "网站可以观看影片或下载资源吗？",
    a: "不可以。本站是影视资料与剧情介绍站点，不提供任何在线播放或下载服务。详情页的播放按钮仅用于演示交互流程，不会加载片源。",
  },
  {
    q: "收藏和浏览记录保存在哪里？",
    a: "收藏与最近浏览都保存在你当前浏览器的本地存储中，不会上传到服务器。清除浏览器数据或更换设备后记录会消失。",
  },
  {
    q: "发现剧情介绍或演员信息有误怎么办？",
    a: "欢迎通过本页表单反馈，选择「内容纠错」类别并注明作品名称与需要修改的地方，编辑会尽快核实。",
  },
  {
    q: "可以转载站内的剧情长文吗？",
    a: "非商业用途转载请注明来源并保留原文链接；商业用途请先通过邮件取得授权。",
  },
];

export default function ContactPage() {
  const { push } = useToast();
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  usePageMeta({
    title: "联系我们 - 华丽的外出影视大全",
    description:
      "联系华丽的外出编辑部：内容纠错、影视投稿、合作咨询与使用反馈。填写表单或通过邮箱联系我们，工作日 10:00-19:00 回复。",
    keywords: "联系我们,影视投稿,内容纠错,华丽的外出,合作咨询",
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const payload = new FormData(formEl);

    const honeypot = String(payload.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      setMessageLength(0);
      return;
    }

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMessage).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isOk) {
        setStatus("error");
        setFormError(
          serverMessage && typeof serverMessage === "string"
            ? serverMessage
            : "提交没有成功，请检查填写内容后重试。",
        );
        return;
      }

      setStatus("success");
      formEl.reset();
      setMessageLength(0);
      push("反馈已提交，感谢你帮助我们把内容做得更准确", "accent");
    } catch {
      setStatus("error");
      setFormError("网络连接异常，暂时无法提交。请稍后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <SiteLayout>
      <div className="shell pt-5 md:pt-7">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="text-foreground-800">联系我们</span>
        </nav>

        <header className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-secondary-50 px-5 py-7 md:px-8 md:py-9">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="h-3 w-1 rounded-full bg-accent-500" />
              <span className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-700">
                Contact
              </span>
            </div>
            <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
              和我们聊聊影视
            </h1>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              发现资料有误、想补充一部作品的剧情，或者只是想说某集拍得很好——都可以从这里告诉我们。
              工作日 10:00 - 19:00，我们会认真读完每一条。
            </p>
          </div>
        </header>

        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
          {CONTACT_CARDS.map((card) => (
            <div
              key={card.title}
              className="rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                <i className={`${card.icon} text-lg`} />
              </span>
              <h2 className="mt-3 text-sm font-bold text-foreground-950">{card.title}</h2>
              <p className="mt-1 break-all font-label text-[13px] font-medium text-foreground-800">
                {card.value}
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">{card.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <section className="lg:col-span-7" aria-label="反馈表单">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 md:text-xl">填写反馈</h2>
              <p className="mt-1 text-xs text-foreground-500">
                带 * 的为必填项。表单提交后会显示结果，不会跳转页面。
              </p>

              {status === "success" ? (
                <div className="anim-float-up mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                    <i className="ri-check-line text-xl" />
                  </span>
                  <h3 className="mt-3 text-base font-bold text-foreground-950">感谢你的反馈</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                    我们已经收到你的留言，会在工作日尽快查看。如果内容需要进一步确认，编辑会通过你留下的邮箱联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="contact-feedback-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="mt-4 space-y-4"
                  noValidate={false}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground-700">
                        姓名 <span className="text-accent-600">*</span>
                      </span>
                      <input
                        type="text"
                        name="name"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-foreground-700">
                        邮箱 <span className="text-accent-600">*</span>
                      </span>
                      <input
                        type="email"
                        name="email"
                        required
                        maxLength={80}
                        placeholder="you@example.com"
                        className="h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                    </label>
                  </div>

                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-foreground-700">反馈类型</span>
                    <div className="relative">
                      <select
                        name="topic"
                        defaultValue="内容纠错"
                        className="h-10 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      >
                        <option value="内容纠错">内容纠错</option>
                        <option value="剧情投稿">剧情投稿</option>
                        <option value="演员资料">演员资料</option>
                        <option value="合作咨询">合作咨询</option>
                        <option value="使用反馈">使用反馈</option>
                      </select>
                      <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                        <i className="ri-arrow-down-s-line text-base" />
                      </span>
                    </div>
                  </label>

                  <label className="flex flex-col gap-1.5">
                    <span className="flex items-center justify-between text-xs font-medium text-foreground-700">
                      <span>
                        留言内容 <span className="text-accent-600">*</span>
                      </span>
                      <span className="font-label text-[11px] font-normal text-foreground-400">
                        {messageLength}/500
                      </span>
                    </span>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="请写下作品名称、需要修改的内容，或你想说的话（500 字以内）"
                      className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </label>

                  <div className="form-extra-field">
                    <label htmlFor="website_alt">Website</label>
                    <input
                      id="website_alt"
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {status === "error" && formError && (
                    <p className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
                      <i className="ri-error-warning-line mt-0.5 text-sm" />
                      <span>{formError}</span>
                    </p>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <p className="text-[11px] leading-relaxed text-foreground-400">
                      提交即表示同意我们使用该信息回复你的反馈，不会用于其他用途。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-base" />
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-base" />
                          提交反馈
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

          <aside className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="h-52 w-full bg-secondary-100">
                <iframe
                  title="华丽的外出编辑部位置地图"
                  src="https://51fldaquan.asia/maps?q=%E4%B8%8A%E6%B5%B7%E5%B8%82%E9%9D%99%E5%AE%89%E5%8C%BA&output=embed"
                  className="h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="p-4">
                <h2 className="text-sm font-bold text-foreground-950">编辑部位置</h2>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  上海市静安区影像街 27 号 3F。地铁 2 号线静安寺站步行约 8 分钟。
                </p>
                <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-time-line text-sm text-foreground-400" />
                  工作日 10:00 - 19:00（节假日除外）
                </p>
              </div>
            </div>

            <section className="mt-4 rounded-lg border border-background-200 bg-background-100/60 p-4" aria-label="常见问题">
              <h2 className="text-sm font-bold text-foreground-950">常见问题</h2>
              <div className="mt-2 divide-y divide-background-200/80">
                {FAQS.map((faq, index) => {
                  const open = openFaq === index;
                  return (
                    <div key={faq.q}>
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? null : index)}
                        aria-expanded={open}
                        className="flex w-full cursor-pointer items-center justify-between gap-3 py-3 text-left"
                      >
                        <span className="text-xs font-medium leading-snug text-foreground-800">{faq.q}</span>
                        <i
                          className={`shrink-0 text-base text-foreground-400 ${
                            open ? "ri-subtract-line" : "ri-add-line"
                          }`}
                        />
                      </button>
                      {open && (
                        <p className="anim-soft-in pb-3 text-xs leading-relaxed text-foreground-600">{faq.a}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}